import { useCallback, useEffect, useMemo, useState } from 'react'
import { Search, Users } from 'lucide-react'
import { apiGet } from '../lib/api'
import { useTenantId } from '../context/SessionContext'
import { EmptyState, Skeleton } from '../components/ui'

const PAGE_SIZE = 50

interface PagedResponse<T> {
  items: T[]
  page: number
  page_size: number
  total: number
}

interface GroupItem {
  id?: string
  claim_value: string
  display_name: string
  idp_id: string | null
  idp_name: string
}

interface GroupsListResponse {
  source: string
  items: GroupItem[]
  page: number
  page_size: number
  total: number
}

interface Member {
  id: string
  group_id: string
  subject: string
  email?: string
  display_name?: string
}

interface UserSummary {
  subject: string
  email?: string
  display_name?: string
  groups: string[]
}

type Mode = 'groups' | 'users'

function initials(name: string) {
  const parts = name.trim().split(/\s+/).filter(Boolean)
  if (parts.length === 0) return '?'
  return parts.map((p) => p[0]).join('').slice(0, 2).toUpperCase()
}

export default function AdminDirectoryPage() {
  const tenantId = useTenantId()
  const [mode, setMode] = useState<Mode>('groups')
  const [query, setQuery] = useState('')

  const [groups, setGroups] = useState<GroupItem[]>([])
  const [groupsLoading, setGroupsLoading] = useState(true)
  const [groupsError, setGroupsError] = useState<string | null>(null)
  const [activeGroupId, setActiveGroupId] = useState<string | null>(null)

  const [members, setMembers] = useState<Member[]>([])
  const [membersLoading, setMembersLoading] = useState(false)
  const [membersError, setMembersError] = useState<string | null>(null)

  const [users, setUsers] = useState<PagedResponse<UserSummary>>({ items: [], page: 1, page_size: PAGE_SIZE, total: 0 })
  const [usersLoading, setUsersLoading] = useState(false)
  const [usersError, setUsersError] = useState<string | null>(null)

  // Load the group list once (Groups mode's facet rail source).
  useEffect(() => {
    if (!tenantId) return
    setGroupsLoading(true)
    setGroupsError(null)
    apiGet<GroupsListResponse>(`/api/v1/tenants/${tenantId}/groups?page_size=200`)
      .then((resp) => {
        setGroups(resp.items)
        setActiveGroupId((prev) => prev ?? resp.items[0]?.id ?? null)
      })
      .catch((e: any) => setGroupsError(e.message))
      .finally(() => setGroupsLoading(false))
  }, [tenantId])

  // Load members whenever the active group changes.
  const loadMembers = useCallback(() => {
    if (!tenantId || !activeGroupId) return
    setMembersLoading(true)
    setMembersError(null)
    apiGet<PagedResponse<Member>>(`/api/v1/tenants/${tenantId}/groups/${activeGroupId}/members?limit=200`)
      .then((resp) => setMembers(resp.items))
      .catch((e: any) => setMembersError(e.message))
      .finally(() => setMembersLoading(false))
  }, [tenantId, activeGroupId])

  useEffect(() => {
    loadMembers()
  }, [loadMembers])

  // Load the flat users list once, when Users mode is first opened.
  const loadUsers = useCallback(() => {
    if (!tenantId) return
    setUsersLoading(true)
    setUsersError(null)
    apiGet<PagedResponse<UserSummary>>(`/api/v1/tenants/${tenantId}/users?limit=${PAGE_SIZE}`)
      .then(setUsers)
      .catch((e: any) => setUsersError(e.message))
      .finally(() => setUsersLoading(false))
  }, [tenantId])

  useEffect(() => {
    if (mode === 'users' && users.items.length === 0 && !usersLoading && !usersError) {
      loadUsers()
    }
  }, [mode, users.items.length, usersLoading, usersError, loadUsers])

  // Groups facet rail: alphabetical by claim value — what an admin scanning
  // for a specific group name expects, and stays sane past a handful of
  // groups (SCIM sync order is arbitrary from an admin's point of view).
  const sortedGroups = useMemo(
    () => [...groups].sort((a, b) => a.claim_value.localeCompare(b.claim_value)),
    [groups],
  )

  const q = query.trim().toLowerCase()
  const filteredGroups = useMemo(() => {
    if (!q) return sortedGroups
    return sortedGroups.filter(
      (g) => g.claim_value.toLowerCase().includes(q) || g.idp_name.toLowerCase().includes(q),
    )
  }, [sortedGroups, q])

  const filteredMembers = useMemo(() => {
    if (!q) return members
    return members.filter(
      (m) =>
        (m.display_name ?? '').toLowerCase().includes(q) ||
        (m.email ?? '').toLowerCase().includes(q) ||
        m.subject.toLowerCase().includes(q),
    )
  }, [members, q])

  const filteredUsers = useMemo(() => {
    if (!q) return users.items
    return users.items.filter(
      (u) =>
        (u.display_name ?? '').toLowerCase().includes(q) ||
        (u.email ?? '').toLowerCase().includes(q) ||
        u.subject.toLowerCase().includes(q) ||
        (u.groups ?? []).some((g) => g.toLowerCase().includes(q)),
    )
  }, [users.items, q])

  const activeGroup = groups.find((g) => g.id === activeGroupId) ?? null

  return (
    <div>
      <div className="mb-4 flex flex-wrap items-center gap-3">
        <div className="relative min-w-[240px] flex-1">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted" />
          <input
            className="w-full rounded-lg border border-border bg-surface py-2 pl-9 pr-3 text-sm text-foreground focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary/40"
            placeholder="Search users or groups…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
        </div>
        <div
          className="inline-flex gap-0.5 rounded-lg border border-border bg-surface-muted p-0.5"
          title="Read-only, synced from your identity provider. Add or remove members there, not here."
        >
          <button
            type="button"
            className={`rounded-md px-3.5 py-1.5 text-sm font-semibold ${
              mode === 'groups' ? 'bg-surface text-foreground shadow-sm' : 'text-muted'
            }`}
            onClick={() => setMode('groups')}
          >
            Groups
          </button>
          <button
            type="button"
            className={`rounded-md px-3.5 py-1.5 text-sm font-semibold ${
              mode === 'users' ? 'bg-surface text-foreground shadow-sm' : 'text-muted'
            }`}
            onClick={() => setMode('users')}
          >
            Users
          </button>
        </div>
      </div>

      {(groupsError || membersError || usersError) && (
        <div className="mb-4 rounded-lg border border-danger/30 bg-danger-muted px-4 py-3 text-sm text-danger-foreground">
          {groupsError || membersError || usersError}
        </div>
      )}

      {mode === 'groups' ? (
        groupsLoading ? (
          <Skeleton className="h-96 w-full" />
        ) : groups.length === 0 ? (
          <EmptyState
            icon={<Users className="h-8 w-8" />}
            title="No SCIM groups yet"
            description="Onboard a SCIM integration from Identity Provider settings, then trigger a Group push from your IdP."
          />
        ) : (
          <div className="grid min-h-[440px] grid-cols-[250px_1fr] overflow-hidden rounded-xl border border-border bg-surface">
            <div className="overflow-y-auto border-r border-border bg-surface-muted p-2.5">
              <div className="px-2 pb-2 pt-1.5 text-[11px] font-bold uppercase tracking-wide text-muted">
                Groups (SCIM)
              </div>
              {filteredGroups.length === 0 ? (
                <p className="px-2 py-3 text-xs text-muted">No groups match your search.</p>
              ) : (
                filteredGroups.map((g) => (
                  <button
                    key={g.id}
                    type="button"
                    onClick={() => setActiveGroupId(g.id ?? null)}
                    className={`mb-0.5 flex w-full items-center justify-between gap-2 rounded-lg border px-2.5 py-2 text-left text-sm ${
                      activeGroupId === g.id
                        ? 'border-border bg-surface shadow-[inset_2px_0_0_theme(colors.primary.DEFAULT)]'
                        : 'border-transparent hover:bg-surface'
                    }`}
                  >
                    <span className="flex min-w-0 flex-col">
                      <span
                        className={`truncate font-semibold ${activeGroupId === g.id ? 'text-primary' : 'text-foreground'}`}
                      >
                        {g.claim_value}
                      </span>
                      <span className="text-[11px] text-muted">{g.idp_name}</span>
                    </span>
                    <span className="flex-shrink-0 rounded-full border border-border bg-background px-1.5 py-0.5 font-mono text-[11px] tabular-nums text-muted">
                      {g.id === activeGroupId ? members.length : ''}
                    </span>
                  </button>
                ))
              )}
            </div>
            <div className="flex min-w-0 flex-col">
              <div className="flex items-baseline justify-between gap-3 border-b border-border px-5 py-4">
                <h2 className="text-sm font-bold text-foreground">{activeGroup?.claim_value ?? '—'}</h2>
                <span className="text-xs text-muted">{activeGroup?.idp_name}</span>
              </div>
              {membersLoading ? (
                <div className="p-5">
                  <Skeleton className="h-40 w-full" />
                </div>
              ) : filteredMembers.length === 0 ? (
                <EmptyState
                  icon={<Users className="h-7 w-7" />}
                  title={q ? `No members match "${query}".` : 'No active members in this group yet.'}
                  className="border-none"
                />
              ) : (
                <div className="flex flex-col">
                  {filteredMembers.map((m) => (
                    <div key={m.id} className="flex items-center gap-3.5 border-b border-border px-5 py-3 last:border-none hover:bg-surface-muted">
                      <span className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full bg-primary/10 text-[11px] font-bold text-primary">
                        {initials(m.display_name || m.subject)}
                      </span>
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-sm font-medium text-foreground">{m.display_name || m.subject}</p>
                        {m.email && <p className="truncate text-xs text-muted">{m.email}</p>}
                      </div>
                      <div className="min-w-0 max-w-[40%]">
                        <p className="truncate font-mono text-xs text-muted" title={m.subject}>
                          {m.subject}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
              <div className="flex items-center justify-between border-t border-border px-5 py-2.5 text-xs text-muted">
                <span>
                  {filteredMembers.length} of {members.length}
                </span>
              </div>
            </div>
          </div>
        )
      ) : usersLoading ? (
        <Skeleton className="h-96 w-full" />
      ) : (
        <div className="overflow-hidden rounded-xl border border-border bg-surface">
          <div className="flex items-baseline justify-between gap-3 border-b border-border px-5 py-4">
            <h2 className="text-sm font-bold text-foreground">All users</h2>
            <span className="text-xs text-muted">
              {users.total} total · searches only this loaded page, not the full directory
            </span>
          </div>
          {filteredUsers.length === 0 ? (
            <EmptyState
              icon={<Users className="h-7 w-7" />}
              title={q ? `No users match "${query}" on this loaded page.` : 'No users in the directory yet.'}
              className="border-none"
            />
          ) : (
            <>
              <div className="grid grid-cols-[30px_1.3fr_1fr_1.6fr] gap-3.5 border-b border-border bg-surface-muted px-5 py-2.5">
                <span />
                <span className="text-[11px] font-bold uppercase tracking-wide text-muted">Name</span>
                <span className="text-[11px] font-bold uppercase tracking-wide text-muted">Subject</span>
                <span className="text-[11px] font-bold uppercase tracking-wide text-muted">Groups</span>
              </div>
              {filteredUsers.map((u) => (
                <div
                  key={u.subject}
                  className="grid grid-cols-[30px_1.3fr_1fr_1.6fr] items-center gap-3.5 border-b border-border px-5 py-3 last:border-none hover:bg-surface-muted"
                >
                  <span className="flex h-7 w-7 items-center justify-center rounded-full bg-primary/10 text-[11px] font-bold text-primary">
                    {initials(u.display_name || u.subject)}
                  </span>
                  <div className="min-w-0">
                    <p className="truncate text-sm font-medium text-foreground">{u.display_name || u.subject}</p>
                    {u.email && <p className="truncate text-xs text-muted">{u.email}</p>}
                  </div>
                  <p className="truncate font-mono text-xs text-muted" title={u.subject}>
                    {u.subject}
                  </p>
                  <div className="flex flex-wrap gap-1.5">
                    {(u.groups ?? []).map((g) => (
                      <span
                        key={g}
                        className="whitespace-nowrap rounded-full border border-border bg-surface-muted px-2 py-0.5 text-[11.5px] font-medium text-foreground"
                      >
                        {g}
                      </span>
                    ))}
                  </div>
                </div>
              ))}
            </>
          )}
          <div className="flex items-center justify-between border-t border-border px-5 py-2.5 text-xs text-muted">
            <span>
              {filteredUsers.length} of {users.items.length}
              {q ? ' matching (this page only)' : ''}
            </span>
          </div>
        </div>
      )}
    </div>
  )
}
